/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import React, { MouseEventHandler, ReactElement, useState } from 'react';
import { useTranslate } from 'hooks/translate';
import slotTaken from 'assets/images/taken.png';
import { useMutation, useQuery } from 'react-query';
import { reserveSlotsApi } from 'api/bestpark/reserve-slots';
import { useReservations } from 'contexts/reservations-context';
import { useAuth } from 'contexts/auth-context';
import useNav from 'hooks/nav';
import { parksApi } from 'api/bestpark/parks';
import { toArray } from 'helpers/utils';
import { ReactComponent as Loader } from 'assets/svgs/loading.svg';

import {
  BOOKING_IDENTIFICATION_PATH,
  BOOKING_INFORMATION_PATH,
  HOMEPAGE_PATH,
} from 'constants/paths';

import './parking.scss';
import { LOADING } from 'constants/query';
import { BOOKING_PAGES } from 'constants/booking';
import Alert from 'components/alerts/alert';
import { sortCampSlots } from 'helpers/camp-helpers';

interface ParkingSlotProps {
  back: MouseEventHandler;
}

const Parking = ({ back }: ParkingSlotProps): ReactElement => {
  // Initial states
  const { locale, t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState('');
  const { setPage } = useReservations();
  const { goTo } = useNav();

  // User
  const { user } = useAuth();

  // Context
  const {
    endDate,
    parkId,
    slot,
    setSlot,
    setSlotFee,
    setSlotName,
    setSlotFeeOveride,
    setIsPersonBasedPricing,
    startDate,
    costBreakdown,
    setCostBreakdown,
  } = useReservations();

  if (!parkId) {
    goTo(HOMEPAGE_PATH);
  }

  // Get park data
  const { data: parkDetail } = useQuery(
    ['park', String(parkId), locale],
    parksApi.getPark,
  );

  // Get available slots
  const { data: slotsData, status: slotsDataStatus } = useQuery(
    [parkId, startDate, endDate, user?.id ?? 0],
    parksApi.getParkingSlotAvailable,
  );

  // Parking map
  const parkMap = parkDetail?.parkMaps?.[0] ?? '';

  const onError = (error: unknown): void => {
    if (error) {
      setLoading(false);
      setErrorMessage(t('errors.default'));
    }
  };

  const onSuccess = (): void => {
    setLoading(false);
    if (user) {
      goTo(`${BOOKING_INFORMATION_PATH}`);
      setPage(BOOKING_PAGES.CONTACT_DETAILS);
    } else {
      goTo(BOOKING_IDENTIFICATION_PATH);
      setPage(BOOKING_PAGES.CONTACT_DETAILS);
    }
  };

  // Submit slot
  const onNextButtonClick = (): void => {
    setLoading(true);

    if (user) {
      postReserveParkingSlots({
        data: {
          customerId: user.id,
          parkId: parkDetail?.id,
          parkingSlot: slot,
          startDate,
          endDate,
        },
      });
      goTo(`${BOOKING_INFORMATION_PATH}`);
      setPage(BOOKING_PAGES.CONTACT_DETAILS);
    } else {
      goTo(BOOKING_IDENTIFICATION_PATH);
      setPage(BOOKING_PAGES.CONTACT_DETAILS);
    }
  };

  // Selecting parking slot
  const onClick = (
    parkSlot: number,
    slotFee: number,
    slotFeeOveride: number,
    slotPersonBasedPricing: boolean,
    slotName: string,
  ): void => {
    if (parkSlot === slot) {
      setSlot(0);
      setSlotFee(0);
      setSlotFeeOveride(0);
      setIsPersonBasedPricing(true);
      setMessage('');
      setSlotName('');
    } else {
      const slotTotal = slotFee * costBreakdown.slotCategory.numberOfNights;
      const slotFeeOverideTotal =
        slotFeeOveride * costBreakdown.slotCategory.numberOfNights;

      const totalCost =
        (slotFeeOveride > 0
          ? slotFeeOverideTotal
          : costBreakdown.base.total + slotTotal) + // camp price // parking slot price
        (slotPersonBasedPricing ? costBreakdown.additionalGuests.total : 0); // additional guests

      setMessage(t('reservation.slots.summary', totalCost.toFixed(2)));
      setSlot(parkSlot);
      setSlotFee(slotFee);
      setSlotFeeOveride(slotFeeOveride);
      setIsPersonBasedPricing(slotPersonBasedPricing);
      setSlotName(slotName);

      setCostBreakdown({
        ...costBreakdown,
        slotCategory: {
          ...costBreakdown.slotCategory,
          cost: slotFeeOveride > 0 ? slotFeeOveride : slotFee,
          total: slotFeeOverideTotal > 0 ? slotFeeOverideTotal : slotTotal,
        },
        grandTotal: costBreakdown.base.total + slotTotal,
      });
    }
  };

  // Render parking map
  const renderParkImage = (
    parkName?: string,
    parkMap?: string,
  ): ReactElement => {
    return (
      <div className='park-map-container'>
        {parkMap && (
          <img
            className='camp-map-image'
            src={`${process.env.REACT_APP_MEDIA_BASE_URL}/${parkMap}`}
            alt='park'
          />
        )}
        <p className='camp-name'>{parkName}</p>
      </div>
    );
  };

  // Render parking slots selection
  const renderParkingSlots = (
    parkSlotCategories?: ParkSlotCategory[],
    parkSlots?: ParkSlot[],
  ): ReactElement => {
    const categorySlots = (
      parkSlotCategory: ParkSlotCategory,
    ): ParkSlot[] | undefined => {
      if (parkSlots) {
        return sortCampSlots(parkSlots).filter(
          (parkSlot) =>
            parkSlot.data.parkSlotCategoryId === parkSlotCategory.category.id,
        );
      } else {
        return [];
      }
    };

    return (
      <div className='category-container'>
        {/* renderDefaultCategory(parkSlotCategories, slotCount) */}
        {parkSlotCategories
          ?.sort((a, b) => (a.data.sort > b.data.sort ? 1 : -1))
          .map((parkSlotCategory: ParkSlotCategory) => {
            const translation = parkSlotCategory.category.translations.find(
              (translate: ParkSlotCategoryTranslations) =>
                translate.languageCode === locale,
            );

            let priceDisplay = '';

            if (parkSlotCategory.category?.priceOveride) {
              priceDisplay = t(
                'reservation.slots.price',
                `${parkSlotCategory.category.priceOveride}`,
              );
            } else if (parkSlotCategory.category?.price) {
              priceDisplay = t(
                'reservation.slots.addPrice',
                `${parkSlotCategory.category.price}`,
              );
            }

            return (
              <div key={parkSlotCategory.category.id} className='camp-slots'>
                {categorySlots(parkSlotCategory)?.length ? (
                  <div className='title'>
                    <h6 className='category-title'>
                      {`${translation?.name} ${priceDisplay}`}
                    </h6>
                    {parkSlotCategory.category.description && (
                      <p className='category-description'>
                        {translation?.description}
                      </p>
                    )}
                  </div>
                ) : (
                  ''
                )}
                {slotsDataStatus === LOADING ? (
                  <Loader />
                ) : (
                  categorySlots(parkSlotCategory)?.map(
                    (parkingSlot: ParkSlot) => {
                      return (
                        <div key={parkingSlot.data.id}>
                          {slotsData?.includes(Number(parkingSlot.slot)) ? (
                            <div
                              className={
                                Number(slot) === Number(parkingSlot.slot)
                                  ? 'slots selected'
                                  : 'slots'
                              }
                              key={parkingSlot.data.id}
                              onClick={
                                () =>
                                  onClick(
                                    Number(parkingSlot.slot),
                                    parkSlotCategory.category.price,
                                    parkSlotCategory.category.priceOveride,
                                    parkSlotCategory.category
                                      .isPersonBasedPricing,
                                    translation?.name ??
                                      parkSlotCategory.category.name,
                                  )
                                /* eslint-disable-next-line func-call-spacing */
                              }
                            >
                              <p className='slot-text'>
                                {parkingSlot.slotNumber || parkingSlot.slot}
                              </p>
                            </div>
                          ) : (
                            <div className='slots taken'>
                              <img
                                className='slots-taken'
                                alt={t('reservation.slots.unavailable')}
                                src={slotTaken}
                              />
                              <p className='slot-text'>{parkingSlot.slot}</p>
                            </div>
                          )}
                        </div>
                      );
                    },
                  )
                )}
              </div>
            );
          })}
      </div>
    );
  };

  // Reserve slots on next
  const [postReserveParkingSlots] = useMutation(
    reserveSlotsApi.postReserveParkingSlots,
    {
      onSuccess,
      onError,
    },
  );

  const hasSlots = (): Boolean => {
    return !!parkDetail?.parkSlots;
  };

  return (
    <div className='camp-selection-container booking-content'>
      <div className='booking-forms'>
        <h1 className='camp-title'>{t('reservation.slots.chooseTitle')}</h1>
        {errorMessage && <p>{errorMessage}</p>}
        <div className='camp-selection'>
          <div className='camp-selection-details'>
            <div className='camp-map-container'>
              {renderParkImage(parkDetail?.name, parkMap)}
            </div>
            {slotsDataStatus === LOADING || !parkDetail ? (
              <Loader />
            ) : (
              <div className='camp-slots-container'>
                {hasSlots() &&
                  renderParkingSlots(
                    toArray(parkDetail?.parkSlotCategories),
                    toArray(parkDetail?.parkSlots),
                  )}
                {message && <Alert message={message} variant='success' />}
              </div>
            )}
          </div>
        </div>
        <button onClick={onNextButtonClick} disabled={loading || !slot}>
          {t('reservation.registration.next')}
        </button>
      </div>
    </div>
  );
};

export default Parking;
